import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type { LegacySocialDefault, LegacySocialZero, SocialEntity, SocialItem } from '@sites/entity-core';
import { socialLegacyDefaultV0Schema, socialLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { shadowPlugin } from '../../plugins/widget/shadow.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type SocialFormModel = SocialEntity;
export type SocialDataModel = SocialEntity['data'];
export type { SocialItem };

/**
 * Social-specific accessors shared verbatim by both variant assemblers — the
 * socials array (with its immutable array sugar) and the shared appearance /
 * layout fields. These are structurally identical on both variants (only
 * `position` differs, and that comes from the position plugins), so the lazy
 * `raw` thunk is narrowed to the default variant's FieldTree internally.
 *
 * Exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model) — composed with {@link composeForm},
 * never object spread.
 *
 * Nullish schema keys (`buttonSize`, `buttonHover`, `isIndependentCorners`,
 * `advancedRadius`, `advancedBorderSettings`) hit the missing-key trap on
 * WRITE (the FieldTree child never materialises for keys absent from the
 * model) — their setters write **through the parent data node**.
 */
function socialSharedAccessors(raw: () => FieldTree<LegacySocialDefault> | FieldTree<LegacySocialZero>) {
    const r = raw as () => FieldTree<LegacySocialDefault>;
    return {
        // Socials array — exposed as a single FieldTree branch plus immutable
        // array sugar. Per-item field access goes through `socialAt(i)` which
        // resolves the indexed child FieldTree (`form.socialAt(0).link`).
        get socials() {
            return r().data.socials;
        },
        /**
         * Current socials array snapshot as a computed signal — template-friendly
         * sugar over `socials().value()` (empty array before the model is seeded).
         */
        socialItems: computed<SocialItem[]>(() => r().data.socials?.().value() ?? []),
        setSocials: (value: SocialItem[]) => {
            r().data.socials().value.set(value);
        },
        socialAt: (index: number): FieldTree<SocialItem> | undefined => {
            return (r().data.socials as unknown as Record<number, FieldTree<SocialItem>>)[index];
        },
        addSocial: (item: SocialItem) => {
            r()
                .data.socials()
                .value.update((arr) => [...arr, item]);
        },
        removeSocial: (index: number) => {
            r()
                .data.socials()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveSocial: (from: number, to: number) => {
            r()
                .data.socials()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateSocial: (index: number, patch: Partial<SocialItem>) => {
            r()
                .data.socials()
                .value.update((arr) => arr.map((s, i) => (i === index ? { ...s, ...patch } : s)));
        },

        // Shared appearance — mirrored onto every item by the editor when changed.
        /**
         * Rendered appearance of the entries (`'icon'` | `'button'`). Named
         * `appearanceType` because the entity field is `data.type`, which would
         * collide with the envelope plugin's `type` discriminator accessor.
         */
        get appearanceType() {
            return r().data.type;
        },
        setAppearanceType: (value: SocialDataModel['type']) => {
            r().data.type().value.set(value);
        },
        get iconType() {
            return r().data.iconType;
        },
        setIconType: (value: SocialDataModel['iconType']) => {
            r().data.iconType().value.set(value);
        },
        get styleType() {
            return r().data.styleType;
        },
        setStyleType: (value: SocialDataModel['styleType']) => {
            r().data.styleType().value.set(value);
        },
        /** Icon size in px — only meaningful for the icon appearance. */
        get height() {
            return r().data.height;
        },
        setHeight: (value: number) => {
            r().data.height().value.set(value);
        },
        /** `undefined` while the key is absent from the persisted data (nullish schema). */
        get buttonSize() {
            return r().data.buttonSize;
        },
        setButtonSize: (value: SocialDataModel['buttonSize']) => {
            r()
                .data()
                .value.update((d) => ({ ...d, buttonSize: value }));
        },
        get buttonBase() {
            return r().data.buttonBase;
        },
        setButtonBase: (value: SocialDataModel['buttonBase']) => {
            r().data.buttonBase().value.set(value);
        },
        /** `undefined` while the key is absent from the persisted data (nullish schema). */
        get buttonHover() {
            return r().data.buttonHover;
        },
        setButtonHover: (value: SocialDataModel['buttonHover']) => {
            r()
                .data()
                .value.update((d) => ({ ...d, buttonHover: value }));
        },
        /** `undefined` while the key is absent from the persisted data (nullish schema). */
        get isIndependentCorners() {
            return r().data.isIndependentCorners;
        },
        setIsIndependentCorners: (value: boolean) => {
            r()
                .data()
                .value.update((d) => ({ ...d, isIndependentCorners: value }));
        },
        /** `undefined` while the key is absent from the persisted data (nullish schema). */
        get advancedRadius() {
            return r().data.advancedRadius;
        },
        setAdvancedRadius: (value: SocialDataModel['advancedRadius']) => {
            r()
                .data()
                .value.update((d) => ({ ...d, advancedRadius: value }));
        },
        /** `undefined` while the key is absent from the persisted data (nullish schema). */
        get advancedBorderSettings() {
            return r().data.advancedBorderSettings;
        },
        setAdvancedBorderSettings: (value: SocialDataModel['advancedBorderSettings']) => {
            r()
                .data()
                .value.update((d) => ({ ...d, advancedBorderSettings: value }));
        },

        // Group layout
        get horizontalAlign() {
            return r().data.horizontalAlign;
        },
        setHorizontalAlign: (value: SocialDataModel['horizontalAlign']) => {
            r().data.horizontalAlign().value.set(value);
        },
        get mobileHorizontalAlign() {
            return r().data.mobileHorizontalAlign;
        },
        setMobileHorizontalAlign: (value: SocialDataModel['mobileHorizontalAlign']) => {
            r().data.mobileHorizontalAlign().value.set(value);
        },
        get tabletHorizontalAlign() {
            return r().data.tabletHorizontalAlign;
        },
        setTabletHorizontalAlign: (value: SocialDataModel['tabletHorizontalAlign']) => {
            r().data.tabletHorizontalAlign().value.set(value);
        },
        get isSynchronizeStyle() {
            return r().data.isSynchronizeStyle;
        },
        /**
         * Style-synchronisation flag with the legacy-null rule applied: very
         * old widgets persisted `null`, which the FormGroup editor treated as
         * "off" — truthy check on purpose.
         */
        isSynchronized: computed<boolean>(() => Boolean(r().data.isSynchronizeStyle?.().value())),
        setIsSynchronizeStyle: (value: boolean) => {
            r().data.isSynchronizeStyle().value.set(value);
        },
    };
}

/**
 * Per-variant accessor assemblers. Exactly like button, the two social
 * variants share every accessor and differ **only** in the `position` branch
 * type: the default form's grid is the simple per-device `WidgetPosition`
 * (via {@link widgetPositionPlugin}), the zero form's is the full
 * `ExtendedPosition` (anchors, heights, changed-flags — via
 * {@link extendedPositionPlugin}).
 *
 * The first `composeForm` part widens the inherited base methods
 * (`syncFromEntity`, `setValue`, `value`) to operate on `SocialEntity` so the
 * dispatcher's union return collapses into a single useful shape for callers
 * that only know the union (e.g. `WebLegacyWidgetSignalFormService`).
 */
function assembleDefaultSocialForm(base: BaseLegacyEntityForm<LegacySocialDefault>) {
    // `_raw` stays typed as `FieldTree<LegacySocialDefault>` so each accessor
    // resolves to the correct nested `FieldTree<…>`.
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<SocialEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        socialSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        shadowPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroSocialForm(base: BaseLegacyEntityForm<LegacySocialZero>) {
    // See {@link assembleDefaultSocialForm} — `_raw` is the narrow
    // `FieldTree<LegacySocialZero>`, which gives `position` its correct
    // `FieldTree<ExtendedPosition>` type.
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<SocialEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        socialSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        shadowPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** social widget. Validates against
 * the legacy default envelope schema, which permits an absent `type` field for
 * parity with pre-discriminator persisted data on disk.
 *
 * **Field accessors are FieldTree references** — exposed via getters so the
 * FieldTree paths materialise lazily (after `syncFromEntity` populates the
 * model). Consumers call them to get the `FieldState`, then read `.value()`,
 * `.touched()`, `.dirty()`, `.errors()` as needed. Convenience `setX(v)`
 * methods are sugar over `field().value.set(v)`; the `socials` array adds
 * `addSocial` / `removeSocial` / `moveSocial` / `updateSocial` /
 * `socialAt(i)` sugar on top.
 *
 * ```ts
 * form.socials().value()           // current SocialItem[]
 * form.socialAt(0)?.link()         // FieldState of the first entry's link
 * form.updateSocial(0, { link })   // immutable per-item patch
 * ```
 */
export function createLegacySocialDefaultForm(options?: BaseLegacyEntityFormOptions<LegacySocialDefault>) {
    const base = createBaseLegacyEntityForm<LegacySocialDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, socialLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // The assembler takes the narrow `BaseLegacyEntityForm<LegacySocialDefault>`
    // so its internal `_raw` keeps the precise FieldTree shape; the public
    // widening to `SocialEntity` happens inside the assembler, via a checked
    // assignment on the base part.
    return assembleDefaultSocialForm(base);
}

/**
 * Signal-form view of a **zero-block-placement** social widget. Validates
 * against the legacy zero envelope schema, which requires `type: 'zero'` on
 * the envelope (the host-app factory copies `meta.type` onto the entity at
 * creation time).
 *
 * Diverges from {@link createLegacySocialDefaultForm} only on `position`:
 * the zero form's grid is the full `ExtendedPosition` (per-device
 * width/height/x/y/rotate/zIndex plus anchor sides) instead of the default
 * variant's simple `WidgetPosition`.
 */
export function createLegacySocialZeroForm(options?: BaseLegacyEntityFormOptions<LegacySocialZero>) {
    const base = createBaseLegacyEntityForm<LegacySocialZero>((schemaPath) => {
        validateStandardSchema(schemaPath, socialLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // See `createLegacySocialDefaultForm` — narrow base in, widening happens
    // inside the assembler.
    return assembleZeroSocialForm(base);
}

/**
 * Variant dispatcher. Callers that only know `SocialEntity` (the union of
 * default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and
 * the dispatcher reads the variant tag from `options.initialData?.type` to
 * pick the matching per-variant factory.
 *
 * The return type is the **union** of the two variant forms. Consumers that
 * want to read variant-specific accessors (the `position` branch types
 * diverge) must narrow via `form.value().type === 'zero'` first, or use the
 * variant factories directly to skip narrowing.
 */
export function createSocialForm(options?: BaseLegacyEntityFormOptions<SocialEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacySocialZeroForm(options as BaseLegacyEntityFormOptions<LegacySocialZero>);
    }

    return createLegacySocialDefaultForm(options as BaseLegacyEntityFormOptions<LegacySocialDefault>);
}

export type SocialForm = ReturnType<typeof createSocialForm>;
export type SocialDefaultForm = ReturnType<typeof createLegacySocialDefaultForm>;
export type SocialZeroForm = ReturnType<typeof createLegacySocialZeroForm>;
